import React, { useState } from 'react'
import BaseModalV2 from 'components/TasksV2/UI/BaseModalV2'
import { FormContext, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { UserFormData } from 'redux/actions/admin'
import { createUser } from 'redux/actions/admin'
import { useToast } from '@chakra-ui/core'
import { updateUser } from 'redux/actions/admin'
import { AppDispatch } from 'config/redux'
import { UserModalProps } from '../../types'
import { ModalHeader } from '../../commonUI/Modal/ModalHeader'
import { ModalFooter } from '../../commonUI/Modal/ModalFooter'
import { UserForm } from '../Forms/UserForm'
import { USER_GROUP_ROLES, USER_PERMISSIONS } from '../utils'
import useLocaleText from 'components/useLocaleText'

const DEFAULT_VALUES = {
  uid: '',
  first_name: '',
  last_name: '',
  password: '',
  email: '',
  phone_number: '',
  country_code: '',
  enable_mfa: false,
  locale: '',
  permission: USER_PERMISSIONS.EDIT,
  default_group: '',
  user_group_role: USER_GROUP_ROLES.WORKER,
  profile_img_url: '',
  avatar_profile_img_url: '',
  desc: '',
  sec_uid: '',
}

export const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  user,
  onCloseConfirmationOpen,
  onGroupModalOpen,
}) => {
  const dispatch: AppDispatch = useDispatch()
  const toast = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const methods = useForm<UserFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      ...(user ? { ...user, password: '' } : DEFAULT_VALUES),
    },
  })

  const isEdit = !!user
  const admin_submit_button = useLocaleText('admin_submit_button')
  const admin_create_user_success = useLocaleText('admin_create_user_success')
  const admin_edit_user_success = useLocaleText('admin_edit_user_success')
  const admin_edit_user = useLocaleText('admin_edit_user')
  const admin_create_user = useLocaleText('admin_create_user')

  const onSubmit = async (data: UserFormData) => {
    setIsLoading(true)
    try {
      if (isEdit) {
        const res = await dispatch(updateUser(data))
        toast({
          title: admin_edit_user_success,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        })
        if (res) {
          onClose()
        }
      } else {
        const res = await dispatch(createUser(data))
        if (res) {
          onClose()
          onGroupModalOpen?.({ uid: res.uid })
        }

        toast({
          title: admin_create_user_success,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        })
      }
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: 'An error occurred while saving data',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseUserModal = () => {
    onCloseConfirmationOpen?.()
  }

  return (
    <FormContext {...methods}>
      <BaseModalV2
        isOpen={isOpen}
        onClose={onClose}
        header={<ModalHeader title={user ? admin_edit_user : admin_create_user} onClose={handleCloseUserModal} />}
        body={<UserForm user={user} isLoading={isLoading} />}
        footer={<ModalFooter isLoading={isLoading} onSubmit={onSubmit} buttonText={admin_submit_button} />}
        modalContentProps={{
          w: '100%',
          maxW: { base: '90%', md: '60vw', lg: '800px' },
          minW: '350px',
          pointerEvents: isLoading ? 'none' : 'auto',
        }}
        modalBodyProps={{
          backgroundColor: 'white !important',
          opacity: isLoading ? 0.8 : 1,
          height: 'auto',
          maxH: '70vh',
        }}
        modalHeaderProps={{ boxShadow: 'none' }}
      />
    </FormContext>
  )
}
